import { useEffect, useRef, useState } from "react";
import { mediaUrl } from "../../mediaAuth";
import { getAutolockBridge } from "../../autolockBridge";

export interface Track {
  filename: string;
  title: string;
  size?: number;
  addedAt?: string;
}

function trackUrl(filename: string): string {
  return mediaUrl(`/assets/music/${encodeURIComponent(filename)}`);
}

// HTMLMediaElement.play() always returns a Promise per spec in real
// browsers, but test environments (jsdom has no real media pipeline) can
// return undefined - guard defensively rather than assume.
function safePlay(audio: HTMLAudioElement): void {
  audio.play()?.catch(() => {});
}

// Same DOM-native single-<audio>-element pattern as the original music.ts,
// just React-owned via a ref instead of a dom.ts export. Passive listening
// shouldn't burn down the idle timer, same as before - routed through
// autolockBridge.ts since autolock.ts itself is dom.ts-coupled and unsafe
// to import directly from React (see autolockBridge.ts).
export function useAudioPlayer() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentFilename, setCurrentFilename] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  // Real playback position/length for a custom progress bar, driven purely
  // by the native <audio> element's own events - never fabricated. Reset
  // to 0 on track change (below) so a new track doesn't briefly show the
  // previous one's stale position/duration before its own metadata loads.
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onPlay = () => {
      setIsPlaying(true);
      getAutolockBridge()?.pauseInactivityForMedia();
    };
    const onPauseOrEnd = () => {
      setIsPlaying(false);
      getAutolockBridge()?.resumeInactivityAfterMedia();
    };
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onDurationChange = () => setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPauseOrEnd);
    audio.addEventListener("ended", onPauseOrEnd);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("durationchange", onDurationChange);
    audio.addEventListener("loadedmetadata", onDurationChange);
    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPauseOrEnd);
      audio.removeEventListener("ended", onPauseOrEnd);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("durationchange", onDurationChange);
      audio.removeEventListener("loadedmetadata", onDurationChange);
    };
  }, []);

  function playTrack(track: Track): void {
    const audio = audioRef.current;
    if (!audio) return;

    if (currentFilename === track.filename) {
      if (audio.paused) safePlay(audio);
      else audio.pause();
      return;
    }

    setCurrentFilename(track.filename);
    setCurrentTime(0);
    setDuration(0);
    audio.src = trackUrl(track.filename);
    safePlay(audio);
  }

  function seek(time: number): void {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(time)) return;
    audio.currentTime = Math.max(0, Math.min(time, audio.duration || time));
    setCurrentTime(audio.currentTime);
  }

  return { audioRef, currentFilename, isPlaying, currentTime, duration, playTrack, seek };
}
